import type { MisconceptionDefinition } from '../types'

/** SPR — Verdubbeling, bakker/jager-regel & verkorten (slice 5). */
export const verdubbelingJagerMisconceptions: MisconceptionDefinition[] = [
  {
    id: 'spr.enkele-medeklinker-lange-klinker',
    label: 'Enkele medeklinker geeft lange klank',
    module: 'SpellingRegel',
    description:
      'Leerling vergeet de medeklinker te verdubbelen, waardoor de klank lang wordt (bijv. "katen" i.p.v. "katten").',
    typicalFamilies: ['SPR-KLANK-BAKKER', 'SPR-KLANK-VERDUBBEL'],
  },
  {
    id: 'spr.verkeerde-lettergreepverdeling',
    label: 'Verkeerde lettergreepverdeling',
    module: 'SpellingRegel',
    description:
      'Leerling verdeelt het woord verkeerd in lettergrepen (bijv. "ka-tten" i.p.v. "kat-ten").',
    typicalFamilies: ['SPR-KLANK-VERDUBBEL'],
  },
  {
    id: 'spr.dubbele-medeklinker-correct',
    label: 'Correct verdubbeld woord als fout gezien',
    module: 'SpellingRegel',
    description:
      'Leerling denkt dat een correct verdubbeld woord (bommen, netten) fout is.',
    typicalFamilies: ['SPR-KLANK-VERDUBBEL'],
  },
  {
    id: 'spr.klinker-verdubbeld-ipv',
    label: 'Klinker verdubbeld i.p.v. medeklinker',
    module: 'SpellingRegel',
    description:
      'Leerling verdubbelt de klinker in plaats van de medeklinker (bijv. "baakken" i.p.v. "bakken").',
    typicalFamilies: ['SPR-KLANK-BAKKER', 'SPR-KLANK-VERDUBBEL'],
  },
  {
    id: 'spr.klinker-en-medeklinker-verdubbeld',
    label: 'Zowel klinker als medeklinker verdubbeld',
    module: 'SpellingRegel',
    description:
      'Leerling verdubbelt zowel de klinker als de medeklinker (bijv. "kooppen" i.p.v. "koppen"). Hybride fout van overcorrectie: één van beide volstaat — bij korte klank alléén de medeklinker.',
    typicalFamilies: ['SPR-KLANK-VERDUBBEL', 'SPR-KLANK-BAKKER'],
  },
  {
    id: 'spr.drie-medeklinkers',
    label: 'Drie medeklinkers i.p.v. twee',
    module: 'SpellingRegel',
    description:
      'Leerling schrijft drie medeklinkers in plaats van twee (bijv. "kopppen" i.p.v. "koppen").',
    typicalFamilies: ['SPR-KLANK-VERDUBBEL'],
  },
  {
    id: 'spr.klinker-veranderd-ipv',
    label: 'Klinker veranderd i.p.v. medeklinker verdubbeld',
    module: 'SpellingRegel',
    description:
      'Leerling verandert de klinker (bijv. "i" → "ie") in plaats van de medeklinker te verdubbelen om de korte klank te behouden.',
    typicalFamilies: ['SPR-KLANK-BAKKER'],
  },
  {
    id: 'spr.gesloten-lettergreep-gesplitst',
    label: 'Medeklinker bij verkeerde lettergreep getrokken',
    module: 'SpellingRegel',
    description:
      'Leerling trekt de medeklinker bij de eerste (gesloten) lettergreep in plaats van bij de tweede (bijv. "bot-en" i.p.v. "bo-ten").',
    typicalFamilies: ['SPR-KLANK-JAGER'],
  },
  {
    id: 'spr.dubbele-medeklinker-lange-klank',
    label: 'Dubbele medeklinker bij meervoud lange klank',
    module: 'SpellingRegel',
    description:
      'Leerling verdubbelt de medeklinker bij het meervoud van een woord met lange klank (bijv. "boot → botten" i.p.v. "boten"). De leerling past de bakker-regel (verdubbeling bij korte klank) toe terwijl de jager-regel (enkele medeklinker, open lettergreep) hoort.',
    typicalFamilies: ['SPR-KLANK-JAGER'],
  },
  {
    id: 'spr.lettergreep-weggelaten',
    label: 'Lettergreep weggelaten',
    module: 'SpellingRegel',
    description:
      'Leerling laat een lettergreep (of de klinker daarvan) weg, waardoor het woord onuitspreekbaar wordt (bijv. "lopn" i.p.v. "lopen", "regn" i.p.v. "regen").',
    typicalFamilies: ['SPR-KLANK-JAGER'],
  },
  {
    id: 'spr.verdubbeling-lange-klank-onnodig',
    label: 'Bakker-regel overgegeneraliseerd naar dubbele klinkers',
    module: 'SpellingRegel',
    description:
      'Leerling past de verdubbel-regel toe op woorden die al een dubbele klinker hebben (bijv. "boom → boommen", "raam → raammen"). Hier is verdubbeling onnodig én onmogelijk: de lange klank wordt al door de dubbele klinker aangegeven. Verwant aan spr.dubbele-medeklinker-lange-klank maar specifiek voor woorden met dubbele klinker in stam.',
    typicalFamilies: ['SPR-KLANK-VERDUBBEL', 'SPR-KLANK-AA-EE-OO'],
  },
  {
    id: 'spr.engelse-ck-spelling',
    label: 'Engelse ck-spelling toegepast',
    module: 'SpellingRegel',
    description:
      'Leerling past de Engelse ck-spelling toe in het Nederlands (bijv. "bakcker" i.p.v. "bakker").',
    typicalFamilies: ['SPR-KLANK-BAKKER'],
  },
  {
    id: 'spr.verkeerde-klinker-stam',
    label: 'Verkeerde klinker uit stam',
    module: 'SpellingRegel',
    description:
      'Leerling gebruikt de verkeerde klinker uit de stam bij verbuiging.',
    typicalFamilies: ['SPR-KLANK-BAKKER'],
  },
  {
    id: 'spr.dubbele-klinker-open-lettergreep',
    label: 'Dubbele klinker behouden in open lettergreep',
    module: 'SpellingRegel',
    description:
      'Leerling behoudt de dubbele klinker in een open lettergreep (bijv. "boomen" i.p.v. "bomen").',
    typicalFamilies: ['SPR-KLANK-VERKORTEN', 'SPR-KLANK-JAGER'],
  },
  {
    id: 'spr.korte-klinker-verdubbeling-verward',
    label: 'Korte klank verward met lange klank',
    module: 'SpellingRegel',
    description:
      'Leerling verwart het meervoud van woorden met korte en lange klanken (bijv. "mannen" vs "manen").',
    typicalFamilies: ['SPR-KLANK-VERKORTEN'],
  },
  {
    id: 'spr.geen-meervoudsuitgang',
    label: 'Meervoudsuitgang vergeten',
    module: 'SpellingRegel',
    description: 'Leerling vergeet de meervoudsuitgang -en toe te voegen.',
    typicalFamilies: ['SPR-KLANK-VERKORTEN'],
  },
  {
    id: 'spr.korte-klinker-verward',
    label: 'Denkt dat de klinker kort is',
    module: 'SpellingRegel',
    description:
      'Leerling denkt dat een lange klinker in een open lettergreep kort is.',
    typicalFamilies: ['SPR-KLANK-VERKORTEN'],
  },
  {
    id: 'spr.medeklinker-veroorzaakt-lange-klinker',
    label: 'Denkt dat medeklinker de lange klank veroorzaakt',
    module: 'SpellingRegel',
    description:
      'Leerling denkt dat de medeklinker achter de klinker de lange klank veroorzaakt, i.p.v. de open lettergreep.',
    typicalFamilies: ['SPR-KLANK-VERKORTEN'],
  },
  {
    id: 'spr.woordsoort-bepaalt-regel',
    label: 'Denkt dat woordsoort de regel bepaalt',
    module: 'SpellingRegel',
    description:
      'Leerling denkt dat de spellingregel afhangt van de woordsoort (werkwoord, zelfstandig naamwoord) i.p.v. het type lettergreep.',
    typicalFamilies: ['SPR-KLANK-VERKORTEN'],
  },
]
